import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Container,
  Paper,
  CssBaseline,
  IconButton,
  InputAdornment,
  useMediaQuery,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";

// Mini logo as base64 image
const MINI_LOGO =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABwAAAAcCAMAAABF0y+mAAAAclBMVEX////+9/f/+/v3wsD50dD61tT97e3yoZ7//Pz629r74N/teHL74+Lugn398vLpVk/3ysjrZmD2vbvmNiz0tbLqXlfzrKnlIxTnRDzjAADypqPkHg786OfviYXoTETnQDjkFwDmOjDlLCDqYVvrbmnxmZaTHOF8AAAAmElEQVR4AdzLxWEDARDAQB0zM3P/JSZm3Ao8b4kfoHCiKghUjRPdQGBaNqiWg8D1VPCDEIER2RAnNoI0CyEvkJSVC1aNJG9isE7zg+v4XKVNC2XXv+WVASdxl4B2ml8N1agCbjWFGLPFu6LyIFwqnX5N+NAvXclWDVBnJp/iddnX2sabc74dc9XotE2EJE1jlDzv/zeE0xkAgDkH9k/cmnwAAAAASUVORK5CYII=";

const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const {
    login,
    error,
    remainingAttempts,
    isAccountLocked,
    lockTimeRemaining,
    isUsernameLocked,
    getUsernameLockTimeRemaining,
  } = useAuth();
  const isMobile = useMediaQuery("(max-width:600px)");
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [displayLockTime, setDisplayLockTime] = useState(lockTimeRemaining);

  // Countdown timer for lock display
  useEffect(() => {
    if (!isAccountLocked || displayLockTime <= 0) {
      return;
    }

    const timer = setInterval(() => {
      setDisplayLockTime((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isAccountLocked, displayLockTime]);

  // Update display lock time when lockTimeRemaining changes
  useEffect(() => {
    setDisplayLockTime(lockTimeRemaining);
  }, [lockTimeRemaining]);

  // Update countdown and clear password when username changes
  useEffect(() => {
    const trimmedUsername = formData.username.trim();

    if (trimmedUsername !== "") {
      // Clear password when username changes
      setFormData((prev) => ({ ...prev, password: "" }));

      // Check if this specific username is locked and get remaining time
      if (isUsernameLocked(trimmedUsername)) {
        // Username is locked, get the actual remaining time
        const remainingSeconds = getUsernameLockTimeRemaining(trimmedUsername);
        setDisplayLockTime(remainingSeconds);
      } else {
        // Username is not locked, clear the display lock time
        setDisplayLockTime(0);
      }
    }
  }, [formData.username]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Prevent submission if current username is locked
    if (isCurrentUsernameLocked) {
      return;
    }

    setLoading(true);
    try {
      await login(formData.username, formData.password);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
  };

  // Format lock time display (e.g., "1:30", "1:05:30", "2:03:45:30")
  const formatLockTime = (seconds: number): string => {
    if (seconds <= 0) return "0:00";

    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (days > 0) {
      return `${days}:${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
    } else if (hours > 0) {
      return `${hours}:${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
    } else {
      return `${minutes}:${String(secs).padStart(2, "0")}`;
    }
  };

  // Check if the current username in the input field is locked
  const isCurrentUsernameLocked =
    formData.username.trim() !== "" &&
    isUsernameLocked(formData.username.trim());

  return (
    <>
      <CssBaseline />
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: (theme) =>
            theme.palette.mode === "dark"
              ? "linear-gradient(135deg, #1e1e1e 0%, #2d2d2d 100%)"
              : "linear-gradient(135deg, #f5f8ff 0%, #e8f0ff 100%)",
          py: 2,
        }}
      >
        <Container maxWidth="sm">
          <Paper
            sx={{
              p: isMobile ? 3 : 4,
              borderRadius: 2,
              boxShadow: (theme) =>
                theme.palette.mode === "dark"
                  ? "0 8px 32px rgba(0,0,0,0.3)"
                  : "0 8px 32px rgba(0,0,0,0.1)",
              backdropFilter: "blur(10px)",
            }}
          >
            {/* Logo and App Name */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mb: 4,
              }}
            >
              <Box
                component="img"
                src={MINI_LOGO}
                alt={t("common.nandemoLogo")}
                sx={{
                  width: 64,
                  height: 64,
                  mb: 2,
                  objectFit: "contain",
                  borderRadius: "50%",
                }}
              />
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  mb: 0.5,
                }}
              >
                Nandemo
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  opacity: 0.6,
                  textAlign: "center",
                }}
              >
                Excelfore Japan
              </Typography>
            </Box>

            {/* Login Form */}
            <form onSubmit={handleSubmit}>
              <TextField
                required
                fullWidth
                label={t("auth.username")}
                margin="normal"
                value={formData.username}
                onChange={(e) =>
                  setFormData({ ...formData, username: e.target.value })
                }
                disabled={loading}
                variant="outlined"
                helperText={
                  isAccountLocked ? t("common.changeUsernameHint") : ""
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    transition: "all 0.3s ease",
                  },
                }}
              />
              <TextField
                required
                fullWidth
                type={showPassword ? "text" : "password"}
                label={t("auth.password")}
                margin="normal"
                value={formData.password}
                onChange={(e) =>
                  setFormData({ ...formData, password: e.target.value })
                }
                disabled={loading || isCurrentUsernameLocked}
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                        sx={{ bgcolor: "transparent" }}
                        disabled={isCurrentUsernameLocked}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    transition: "all 0.3s ease",
                  },
                }}
              />
              {error && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    borderRadius: 1,
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(244, 67, 54, 0.1)"
                        : "rgba(244, 67, 54, 0.05)",
                    border: "1px solid",
                    borderColor: "error.main",
                  }}
                >
                  <Typography color="error" variant="body2" sx={{ mb: 1 }}>
                    {error}
                  </Typography>
                  {isCurrentUsernameLocked && displayLockTime > 0 && (
                    <Typography
                      color="error"
                      variant="caption"
                      sx={{ display: "block", mt: 1, fontWeight: 600 }}
                    >
                      ⏱️ Try again in {formatLockTime(displayLockTime)}
                    </Typography>
                  )}
                  {!isCurrentUsernameLocked &&
                    !isAccountLocked &&
                    remainingAttempts > 0 &&
                    remainingAttempts < 3 && (
                      <Typography
                        color="warning.main"
                        variant="caption"
                        sx={{ display: "block", mt: 1 }}
                      >
                        ⚠️ {remainingAttempts} attempt
                        {remainingAttempts !== 1 ? "s" : ""} remaining before
                        account lock
                      </Typography>
                    )}
                </Box>
              )}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  mt: 3,
                  py: 1.5,
                  fontSize: "1rem",
                  fontWeight: 600,
                  textTransform: "none",
                  borderRadius: 1,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: isCurrentUsernameLocked
                      ? "none"
                      : "translateY(-2px)",
                    boxShadow: (theme) =>
                      isCurrentUsernameLocked
                        ? "none"
                        : `0 8px 16px ${theme.palette.secondary.main}40`,
                  },
                }}
                disabled={loading || isCurrentUsernameLocked}
              >
                {isCurrentUsernameLocked
                  ? `${t("auth.login")} (${formatLockTime(displayLockTime)})`
                  : loading
                    ? t("auth.logging-in")
                    : t("auth.login")}
              </Button>
            </form>
          </Paper>
        </Container>
      </Box>
    </>
  );
};

export default LoginPage;
